import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../layouts';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { isAdminUser } from '../utils/adminAccess';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';

export default function MobileProfileMenuPage() {
  const navigate = useNavigate();
  const { user, dbProfile, signOut } = useAuth();
  const isAdmin = isAdminUser(user);
  const paywallEnabled = useFeatureFlag('PAYWALL');

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const menuItems = [
    {
      label: 'Profile',
      path: '/profile',
      icon: UserCircleIcon,
      description: 'Manage your profile',
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: Cog6ToothIcon,
      description: 'App preferences',
    },
    ...(paywallEnabled
      ? [
          {
            label: 'Billing',
            path: '/billing',
            icon: CreditCardIcon,
            description: 'Plans and payments',
          },
        ]
      : []),
    {
      label: 'Legal',
      path: '/legal',
      icon: DocumentTextIcon,
      description: 'Terms and policies',
    },
    ...(isAdmin
      ? [
          {
            label: 'Admin',
            path: '/admin',
            icon: ShieldCheckIcon,
            description: 'Feature flags and dashboards',
          },
        ]
      : []),
  ];

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-md mx-auto px-4 py-8">
        {/* User Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center space-x-4">
            {dbProfile?.image || user?.photoURL ? (
              <img
                src={dbProfile?.image || user?.photoURL || ''}
                alt={dbProfile?.name || user?.displayName || 'User'}
                className="w-16 h-16 rounded-full ring-2 ring-cyan-400"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-xl font-bold ring-2 ring-cyan-400">
                {(dbProfile?.name || user?.displayName || user?.email || 'U')
                  .charAt(0)
                  .toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-white truncate">
                {dbProfile?.name || user?.displayName || 'User'}
              </h1>
              <p className="text-sm text-slate-400 truncate">
                {dbProfile?.email || user?.email}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Menu Items */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="space-y-3"
        >
          {menuItems.map((item, index) => (
            <motion.button
              key={item.path}
              onClick={() => navigate(item.path)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
              className="w-full flex items-center space-x-4 p-4 bg-slate-800/50 border border-slate-700/30 hover:bg-slate-700/50 hover:border-cyan-400/30 rounded-xl transition-all active:scale-[0.98]"
            >
              <div className="flex-shrink-0">
                <item.icon className="h-6 w-6 text-cyan-400" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium text-white">{item.label}</div>
                <div className="text-xs text-slate-400">{item.description}</div>
              </div>
              <svg
                className="w-5 h-5 text-slate-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </motion.button>
          ))}
        </motion.div>

        {/* Sign In/Out Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="mt-6"
        >
          {user ? (
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center space-x-3 p-4 bg-red-600/10 border border-red-500/30 hover:bg-red-600/20 hover:border-red-500/50 text-red-400 font-medium rounded-xl transition-all active:scale-[0.98]"
            >
              <ArrowRightOnRectangleIcon className="h-6 w-6" />
              <span>Sign Out</span>
            </button>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="w-full flex items-center justify-center space-x-3 p-4 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-xl transition-all active:scale-[0.98]"
            >
              <ArrowLeftOnRectangleIcon className="h-6 w-6" />
              <span>Sign In</span>
            </button>
          )}
        </motion.div>
        </div>
      </div>
    </MainLayout>
  );
}
